import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Search,
  Filter,
  Download,
  Trash2,
  LogOut,
  ArrowLeft,
  Inbox,
  MailCheck,
  CheckCircle2,
  Loader2,
  Mail,
  Phone,
  Building2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "hr_admin_token";

const STATUS_META = {
  new: { label: "New", cls: "border-[var(--gold)]/40 bg-[var(--gold)]/10 text-[var(--gold-bright)]" },
  contacted: { label: "Contacted", cls: "border-sky-400/30 bg-sky-400/10 text-sky-300" },
  closed: { label: "Closed", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" },
};

const AdminInquiries = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tokenInput, setTokenInput] = useState("");

  const [contacts, setContacts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortDesc, setSortDesc] = useState(true);

  const authedFetch = useMemo(
    () =>
      axios.create({
        baseURL: API,
        headers: { "X-Admin-Token": token },
      }),
    [token]
  );

  // Attempt auth
  useEffect(() => {
    if (!token) {
      setAuthed(false);
      return;
    }
    let mounted = true;
    setChecking(true);
    axios
      .get(`${API}/admin/contacts/summary`, { headers: { "X-Admin-Token": token } })
      .then(() => mounted && setAuthed(true))
      .catch(() => {
        if (!mounted) return;
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Invalid admin token.");
      })
      .finally(() => mounted && setChecking(false));
    return () => {
      mounted = false;
    };
  }, [token]);

  // Load data
  const load = async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [contactsR, summaryR] = await Promise.all([
        authedFetch.get("/admin/contacts", { params: { limit: 500 } }),
        authedFetch.get("/admin/contacts/summary"),
      ]);
      setContacts(contactsR.data);
      setSummary(summaryR.data);
    } catch (e) {
      toast.error("Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogin = (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setContacts([]);
    setSummary(null);
    setAuthed(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await authedFetch.patch(`/admin/contacts/${id}/status`, { status });
      toast.success(`Marked as ${status}.`);
      await load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await authedFetch.delete(`/admin/contacts/${id}`);
      toast.success("Inquiry deleted.");
      await load();
    } catch {
      toast.error("Could not delete inquiry.");
    }
  };

  const exportCsv = () => {
    // Direct download with token via fetch → blob (headers won't work with anchor)
    fetch(`${API}/admin/contacts/export.csv`, {
      headers: { "X-Admin-Token": token },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "hr_enterprises_inquiries.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("CSV export failed."));
  };

  const filtered = useMemo(() => {
    let list = contacts.slice();
    if (statusFilter !== "all") list = list.filter((c) => (c.status || "new") === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        [c.name, c.email, c.company, c.phone, c.service, c.message]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      );
    }
    list.sort((a, b) => {
      const av = new Date(a.created_at).getTime();
      const bv = new Date(b.created_at).getTime();
      return sortDesc ? bv - av : av - bv;
    });
    return list;
  }, [contacts, search, statusFilter, sortDesc]);

  // Login form
  if (!authed) {
    return (
      <main
        data-testid="admin-login"
        className="relative flex min-h-screen items-center justify-center bg-[#060606] px-6"
      >
        <div className="aurix-aura" />
        <div className="relative w-full max-w-md rounded-3xl border border-white/[0.08] bg-[#0d0d0f]/90 p-8 shadow-[0_40px_120px_-20px_rgba(212,175,55,0.35)] backdrop-blur-xl">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 text-[13px] text-zinc-400 transition-colors hover:text-[var(--gold-bright)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
          <div className="font-mono-label mb-3 text-[var(--gold)]">
            Restricted · Admin only
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white">
            Inquiries Dashboard
          </h1>
          <p className="mt-2 text-[14px] text-zinc-400">
            Enter the admin access token to view submitted inquiries.
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-3" data-testid="admin-login-form">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Admin token"
              data-testid="admin-token-input"
              className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-[14px] text-white placeholder:text-zinc-500 outline-none transition-colors focus:border-[var(--gold)]/60"
            />
            <button
              type="submit"
              disabled={checking || !tokenInput.trim()}
              data-testid="admin-login-submit"
              className="btn-gold w-full justify-center disabled:opacity-70"
            >
              {checking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                "Unlock Dashboard"
              )}
            </button>
          </form>
          <p className="mt-4 text-[11.5px] text-zinc-500">
            Configure the token via the <code className="text-zinc-400">ADMIN_TOKEN</code> env variable on the backend.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-inquiries"
      className="relative min-h-screen bg-[#0a0a0a] pb-16 pt-10"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <Link
              to="/"
              className="mb-3 inline-flex items-center gap-2 text-[13px] text-zinc-400 transition-colors hover:text-[var(--gold-bright)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to HR ENTERPRISES
            </Link>
            <h1 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
              Inquiries Dashboard
            </h1>
            <p className="mt-2 text-[14px] text-zinc-400">
              Manage every incoming lead from the contact form.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="btn-ghost"
            >
              <Download className="h-4 w-4" strokeWidth={1.8} />
              Export CSV
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="btn-ghost"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.8} />
              Logout
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { key: "total", label: "Total", value: summary?.total ?? "—", icon: Inbox, tone: "text-white" },
            { key: "new", label: "New", value: summary?.new ?? "—", icon: MailCheck, tone: "text-[var(--gold-bright)]" },
            { key: "contacted", label: "Contacted", value: summary?.contacted ?? "—", icon: Mail, tone: "text-sky-300" },
            { key: "closed", label: "Closed", value: summary?.closed ?? "—", icon: CheckCircle2, tone: "text-emerald-300" },
          ].map((s) => (
            <div
              key={s.key}
              data-testid={`admin-summary-${s.key}`}
              className="rounded-2xl border border-white/[0.06] bg-[#0e0e10] p-5"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-zinc-500">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.tone}`} strokeWidth={1.7} />
              </div>
              <div className={`mt-3 font-display text-3xl font-semibold ${s.tone}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-white/[0.06] bg-[#0e0e10] p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company, message…"
              data-testid="admin-search"
              className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-10 py-2.5 text-[13.5px] text-white placeholder:text-zinc-500 outline-none focus:border-[var(--gold)]/60"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-zinc-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-status-filter"
              className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[13.5px] text-white outline-none focus:border-[var(--gold)]/60"
            >
              <option value="all">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="closed">Closed</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDesc((v) => !v)}
              className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[13px] text-zinc-300 transition-colors hover:border-[var(--gold)]/40"
            >
              Sort: {sortDesc ? "Newest first" : "Oldest first"}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0e0e10]">
          {loading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading inquiries…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-zinc-500">
              No inquiries match your filters.
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {filtered.map((c) => {
                const st = STATUS_META[c.status || "new"];
                return (
                  <div
                    key={c.id}
                    data-testid={`admin-row-${c.id}`}
                    className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-[1.5fr_1.6fr_1.4fr_auto] lg:items-start"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-mono-label rounded-full border px-2 py-0.5 text-[9px] ${st.cls}`}
                        >
                          {st.label}
                        </span>
                        <span className="text-[11px] text-zinc-500">
                          {new Date(c.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-2 font-display text-[16px] font-semibold text-white">
                        {c.name}
                      </div>
                      {c.company && (
                        <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-zinc-400">
                          <Building2 className="h-3.5 w-3.5" strokeWidth={1.7} />
                          {c.company}
                        </div>
                      )}
                      {c.service && (
                        <div className="mt-1 text-[12px] text-[var(--gold-bright)]/80">
                          {c.service}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1.5 text-[13px]">
                      <a
                        href={`mailto:${c.email}`}
                        className="flex items-center gap-2 text-zinc-300 transition-colors hover:text-[var(--gold-bright)]"
                      >
                        <Mail className="h-3.5 w-3.5" strokeWidth={1.7} />
                        {c.email}
                      </a>
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          className="flex items-center gap-2 text-zinc-300 transition-colors hover:text-[var(--gold-bright)]"
                        >
                          <Phone className="h-3.5 w-3.5" strokeWidth={1.7} />
                          {c.phone}
                        </a>
                      )}
                    </div>
                    <div className="text-[13px] leading-relaxed text-zinc-300">
                      {c.message}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <select
                        value={c.status || "new"}
                        onChange={(e) => updateStatus(c.id, e.target.value)}
                        data-testid={`admin-row-status-${c.id}`}
                        className="rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white outline-none focus:border-[var(--gold)]/60"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => deleteContact(c.id)}
                        data-testid={`admin-row-delete-${c.id}`}
                        className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 bg-white/[0.02] text-zinc-400 transition-colors hover:border-red-400/40 hover:text-red-400"
                        title="Delete inquiry"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.7} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default AdminInquiries;
